<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, reactive, computed } from 'vue'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn' // 导入中文语言包
import { useUserInfoStore } from '@/stores/modules/user'
import { gsap } from 'gsap'
import type { FormInstance, FormRules } from 'element-plus'
import router from '@/router'

// 设置本地化为中文
dayjs.locale('zh-cn')

const userStore = useUserInfoStore()
const showInput = ref(false)
const ruleFormRef = ref<FormInstance>()
const form = reactive({
  password: ''
})

// 定义一个ref来存储当前时间的字符串表示
const year = ref() // 获取年份
const month = ref() // 获取月份（注意：month() 返回的是 0-11 的整数，需要加 1 转换为标准的 1-12）
const day = ref() // 获取日
const week = ref() // 获取星期
const hour = ref() // 获取小时（24小时制）
const minute = ref() // 获取分钟
const second = ref() // 获取秒

// 更新当前时间的函数
const updateTime = () => {
  year.value = dayjs().year()
  month.value = dayjs().month() + 1
  day.value = dayjs().date()
  week.value = dayjs().format('dddd')
  hour.value = dayjs().hour()
  minute.value = dayjs().minute()
  second.value = dayjs().second()
}

// 获取当前是 AM 还是 PM
const timePeriod = computed(() => {
  return hour.value < 12 ? 'AM' : 'PM'
})

// 更新时间的定时器引用
let timerId: number

// 在组件挂载后开始定时更新
onMounted(() => {
  updateTime() // 初始更新一次
  timerId = setInterval(updateTime, 1000) // 每隔1秒更新一次
})

// 组件卸载前清除定时器，防止内存泄漏
onBeforeUnmount(() => {
  clearInterval(timerId)
})

const padZeroToSingleDigit = (num: number) => {
  return num < 10 ? '0' + num : num
}

const rules = reactive<FormRules<typeof form>>({
  password: [{ required: true, message: 'Please input password', trigger: 'blur' }]
})

const checkPassword = () => {
  // 这里是验证密码的逻辑
  if (form.password === userStore.lockInfo.password) {
    userStore.lockInfo.isLockScreen = false
    userStore.lockInfo.password = ''
  } else {
    // 密码错误，可以做一些提示
    ElMessage({
      message: '密码错误，请重新输入',
      type: 'error',
      plain: true
    })
  }
}

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return
  formEl.validate((valid) => {
    if (valid) {
      checkPassword()
    } else {
      return false
    }
  })
}

// 退出登录
// const handleLogout = () => {
//   userStore.logout().then(() => {
//     resetForm(ruleFormRef.value)
//     router.push('/login')
//   })
// }

const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return
  formEl.resetFields()
}

const lockScreen = (lockState: boolean) => {
  if (lockState) {
    gsap.to('.time-container', { y: 0 })
    gsap.to('.unlock', { y: 0 })
    gsap.to('.unlock-form', { opacity: 0 })
    showInput.value = false
    resetForm(ruleFormRef.value)
  } else {
    gsap.to('.time-container', { y: -50 })
    gsap.to('.unlock', { y: 20 })
    gsap.to('.unlock-form', { opacity: 1 })
    showInput.value = true
  }
}
</script>
<template>
  <div v-if="userStore.lockInfo.isLockScreen" class="lock-screen">
    <div class="time-container">
      <div class="time-item">
        <div class="time-text">
          <span class="time"> {{ padZeroToSingleDigit(hour) }}</span>
          <span class="text"> Hours</span>
        </div>
      </div>
      <div class="colon">:</div>
      <div class="time-item">
        <div class="time-text">
          <span class="time"> {{ padZeroToSingleDigit(minute) }}</span>
          <span class="text"> Minutes</span>
        </div>
      </div>
      <div class="colon">:</div>
      <div class="time-item">
        <div class="time-text">
          <span class="time"> {{ padZeroToSingleDigit(second) }}</span>
          <span class="text"> Seconds</span>
        </div>
        <span class="am-pm">{{ timePeriod }}</span>
      </div>
      <div class="date">
        <span>
          {{ year }} / {{ padZeroToSingleDigit(month) }} / {{ padZeroToSingleDigit(day) }}
        </span>
        <span>
          {{ week }}
        </span>
      </div>
    </div>
    <el-form
      class="unlock-form"
      ref="ruleFormRef"
      :model="form"
      :rules="rules"
      label-width="80px"
      :inline="false"
    >
      <el-form-item prop="password" label-width="auto">
        <el-input
          class="password-input"
          type="password"
          v-model="form.password"
          placeholder="请输入密码"
          clearable
          show-password
        >
          <template #prefix>
            <el-icon>
              <lock />
            </el-icon>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item :required="false" label-width="auto">
        <el-button type="primary" class="unlock-button" link @click="submitForm(ruleFormRef)">
          解锁
        </el-button>
        <el-button type="primary" class="unlock-button" link @click="lockScreen(true)">
          返回
        </el-button>
        <!-- <el-button type="primary" class="unlock-button" link @click="handleLogout()">
          忘记密码
        </el-button> -->
      </el-form-item>
    </el-form>
    <div class="unlock">
      <el-icon v-if="showInput" :size="30" @click="lockScreen(true)"><Unlock /></el-icon>
      <el-icon v-else :size="30" @click="lockScreen(false)"><Lock /></el-icon>
    </div>
  </div>
</template>
<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700&display=swap');

.lock-screen {
  @apply fixed top-0 left-0 w-full h-full bg-[#f0f8ff] z-10 flex flex-col justify-center items-center;
  font-family: 'Orbitron', sans-serif;
}

.time-container {
  @apply relative flex justify-center items-center space-x-3 w-[560px] h-[220px] bg-white rounded-2xl shadow-lg;
}
.theme-icon {
  @apply absolute top-[10px] right-[20px];
}
.time-item {
  @apply relative flex justify-center items-center bg-[#f0f8ff] w-[100px] h-[100px]  rounded-2xl shadow-lg;
}
.time-text {
  @apply flex flex-col justify-center items-center;
}
.time {
  @apply text-[35px] font-[600];
}
.text {
  @apply text-[12px] font-[700] tracking-[2px];
}
.colon {
  @apply text-[40px] font-[600];
}
.am-pm {
  @apply absolute top-0 right-[-50px] text-[20px] font-[700] tracking-[1px];
}
.date {
  @apply absolute bottom-2 flex justify-center items-center space-x-10;
}
.unlock {
  @apply mt-5 cursor-pointer;
}

.unlock-form {
  @apply w-[400px] opacity-0 flex justify-center items-center space-x-2;
}
:deep(.el-form-item__content) {
  @apply flex flex-nowrap justify-center items-center;
}
.unlock-button {
  @apply text-lg tracking-[2px] mx-2 cursor-pointer;
}
.password-input {
  @apply w-[300px];
}
</style>
